/**
 * The "completeness" program: what a profile must carry before a visitor —
 * or an AI assistant answering on their behalf — can actually decide.
 *
 * Why this module exists, measured against the 587 published venues:
 *
 *   | evidence a decision needs   | venues that have it |
 *   | --------------------------- | ------------------- |
 *   | 5+ photos                   |  16   (3%)          |
 *   | a price, any price          |  19   (3%)          |
 *   | floor area in m2            |   9   (2%)          |
 *   | any seating layout          |   7   (1%)          |
 *   | 5+ amenities                | 504  (86%)          |
 *   | parking answered            | 412  (70%)          |
 *   | rental rules                | 503  (86%)          |
 *
 * 571 of 587 profiles carry exactly one photo. That is the whole gap: we
 * aggregate more venues than anyone, and then lose to a single hotel page
 * that shows price, capacity and a gallery.
 *
 * Two things the previous scorer got backwards, both fixed here:
 *
 * 1. Only `pricePerHour > 0` earned the price points, so a host who honestly
 *    declares "cena na vyžiadanie" scored the same zero as one who said
 *    nothing at all. 97% of the catalogue is price-on-request; the formula
 *    punished the exact behaviour we want. A *declared* on-request price now
 *    scores in full. Silence still scores nothing — that is the real defect.
 * 2. A Facebook link earned points while parking, rental rules and the
 *    verification date earned none. Points now go only to evidence a guest
 *    uses to choose.
 *
 * Shared as .mjs so the React wizard, the listing card and the build scripts
 * all score a profile the same way. A second copy of these rules is how the
 * wizard ended up showing 100% next to a bar drawn at 95%.
 */

/** Photos below this are not evidence, they are a placeholder. */
export const PHOTOS_TARGET = 5;
export const PHOTOS_PARTIAL = 3;

/** How long a confirmation counts as current. */
export const FRESH_DAYS = 180;
export const STALE_DAYS = 365;

/** Amenity count that reads as a real equipment list rather than a token. */
export const AMENITIES_TARGET = 5;

/** Score at which a profile earns the public AI READY badge. */
export const AI_READY_SCORE = 90;

export const TIERS = [
  { id: 'ai_ready', min: AI_READY_SCORE },
  { id: 'complete', min: 75 },
  { id: 'standard', min: 50 },
  { id: 'basic', min: 0 },
];

const num = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const text = (value) => String(value ?? '').trim();

const daysSince = (value, now) => {
  const then = Date.parse(text(value));
  if (!Number.isFinite(then)) return null;
  return Math.floor((now - then) / 86400000);
};

/**
 * The five requirements, in the order a host should tackle them. Each returns
 * earned points plus the numbers its copy needs, so the UI never re-derives
 * "how many photos are missing" and never disagrees with the score.
 *
 * `step` is the wizard step that fixes it, so a checklist row can jump there.
 */
const REQUIREMENTS = [
  {
    id: 'photos',
    weight: 25,
    step: 'media',
    evaluate: (p) => {
      const have = Array.isArray(p.images) ? p.images.filter(Boolean).length : 0;
      const detail = { have, need: PHOTOS_TARGET };
      if (have >= PHOTOS_TARGET) return { points: 25, state: 'met', detail };
      // A gallery link is a fallback, not a substitute: it is off-site, we
      // cannot show it in a card, and a crawler will not follow it.
      if (have >= PHOTOS_PARTIAL) return { points: 15, state: 'partial', detail };
      if (have > 0 || text(p.externalGalleryUrl)) return { points: 6, state: 'partial', detail };
      return { points: 0, state: 'missing', detail };
    },
  },
  {
    id: 'price',
    weight: 20,
    step: 'pricing',
    evaluate: (p) => {
      const value = num(p.priceValue);
      if (value > 0) return { points: 20, state: 'met', detail: { mode: 'amount', value } };
      // Declaring "on request" is an answer. Leaving it blank is not, even
      // though the database stores both as price 0.
      if (p.priceDeclaredOnRequest) return { points: 20, state: 'met', detail: { mode: 'on_request' } };
      return { points: 0, state: 'missing', detail: { mode: 'unset' } };
    },
  },
  {
    id: 'capacity',
    weight: 20,
    step: 'details',
    evaluate: (p) => {
      const layouts = ['theater', 'school', 'banquet', 'cocktail']
        .filter((key) => num(p.seating && p.seating[key]) > 0);
      const hasGuests = num(p.maxGuests) > 0;
      const hasArea = num(p.areaSquareMeters) > 0;
      const points = (hasGuests ? 5 : 0) + (hasArea ? 5 : 0) + (layouts.length > 0 ? 10 : 0);
      const detail = { layouts: layouts.length, hasGuests, hasArea };
      if (points >= 20) return { points, state: 'met', detail };
      return { points, state: points > 0 ? 'partial' : 'missing', detail };
    },
  },
  {
    id: 'equipment',
    weight: 20,
    step: 'details',
    evaluate: (p) => {
      const amenities = Array.isArray(p.amenities) ? p.amenities.filter(Boolean).length : 0;
      const hasRules = text(p.terms).length >= 20;
      // Parking is deliberately NOT scored, though the guide still asks for it.
      // The form models it as one checkbox, so "no parking" and "never
      // answered" are the same `false` — scoring it would hand every profile
      // the points for free. It is also the least of the gaps (70% answered).
      // Make it a real yes/no/unanswered control and it can earn points.
      const points = (amenities >= AMENITIES_TARGET ? 10 : amenities > 0 ? 5 : 0)
        + (hasRules ? 10 : 0);
      const detail = { amenities, need: AMENITIES_TARGET, hasRules };
      if (points >= 20) return { points, state: 'met', detail };
      return { points, state: points > 0 ? 'partial' : 'missing', detail };
    },
  },
  {
    id: 'freshness',
    weight: 15,
    // Not a step: the date is stamped on save. In the wizard this reads as
    // met, which is accurate — the profile IS current the moment it is
    // published. It goes stale later, and that is when it becomes an action.
    step: null,
    evaluate: (p, now) => {
      const age = daysSince(p.lastVerifiedAt, now);
      const detail = { age, freshDays: FRESH_DAYS };
      if (age === null) return { points: 0, state: 'missing', detail };
      if (age <= FRESH_DAYS) return { points: 15, state: 'met', detail };
      if (age <= STALE_DAYS) return { points: 7, state: 'partial', detail };
      return { points: 0, state: 'missing', detail };
    },
  },
];

/**
 * @param {object} profile normalized via listingToProfile / formValuesToProfile
 * @param {number} [now] epoch ms, injectable so tests do not depend on the clock
 * @returns {{score: number, tier: string, requirements: Array, missing: Array, nextAction: object|null}}
 */
export function evaluateCompleteness(profile, now = Date.now()) {
  const requirements = REQUIREMENTS.map((requirement) => {
    const result = requirement.evaluate(profile || {}, now);
    return {
      id: requirement.id,
      weight: requirement.weight,
      step: requirement.step,
      points: result.points,
      state: result.state,
      detail: result.detail,
    };
  });

  const score = Math.min(100, Math.round(requirements.reduce((sum, r) => sum + r.points, 0)));
  const tier = (TIERS.find((t) => score >= t.min) || TIERS[TIERS.length - 1]).id;

  // Biggest win first: the requirement with the most points still on the table.
  const missing = requirements
    .filter((r) => r.state !== 'met')
    .sort((a, b) => (b.weight - b.points) - (a.weight - a.points));

  return { score, tier, requirements, missing, nextAction: missing[0] || null };
}

/** A saved Listing (React or normalized API row) -> the scorable shape. */
export function listingToProfile(listing) {
  const d = (listing && listing.spaceDetails) || {};
  return {
    images: listing?.images,
    externalGalleryUrl: d.externalGalleryUrl,
    priceValue: num(d.pricePerHour) || num(listing?.price),
    priceDeclaredOnRequest: d.priceType === 'on_request',
    maxGuests: d.maxGuests,
    areaSquareMeters: d.areaSquareMeters,
    seating: d.seatingCapacities,
    amenities: d.amenities,
    terms: d.terms,
    lastVerifiedAt: listing?.trust?.lastVerifiedAt || listing?.last_verified_at || listing?.updatedAt,
  };
}

/**
 * Live wizard values -> the scorable shape.
 *
 * This is the only place the form's field names are mapped for scoring. The
 * wizard used to build that object inline, twice, and the two copies differed —
 * the number counted social links and the progress bar did not, so a host with
 * a website saw "100%" above a bar drawn at 95%.
 */
export function formValuesToProfile(values, now = Date.now()) {
  const v = values || {};
  return {
    images: v.images,
    externalGalleryUrl: v.externalGalleryUrl,
    priceValue: num(v.pricePerHour),
    priceDeclaredOnRequest: v.priceType === 'on_request',
    maxGuests: v.maxGuests,
    areaSquareMeters: v.areaSquareMeters,
    seating: {
      theater: v.seatingTheater,
      school: v.seatingSchool,
      banquet: v.seatingBanquet,
      cocktail: v.seatingCocktail,
    },
    amenities: v.amenities,
    terms: v.terms,
    // Saving stamps the confirmation date, so the wizard scores the profile
    // as it will read the moment it is published — otherwise 100 would be
    // unreachable while editing and the target would look like a lie.
    lastVerifiedAt: new Date(now).toISOString(),
  };
}

/**
 * Rank listings best-documented first.
 *
 * Shared because two surfaces must agree: the React homepage renders the
 * ranking, and scripts/prerender-listings.mjs writes the static fallback that
 * crawlers read at the same URL. A second copy of this comparator is how the
 * two would silently drift — the failure mode this repo has paid for
 * repeatedly.
 *
 * Ties break toward the richer gallery, then the newer venue, so the order is
 * stable rather than dependent on the input array.
 *
 * @param {Array<object>} listings normalized listings or API rows
 * @returns {Array<object>} a new array, highest score first
 */
export function rankByCompleteness(listings) {
  const photos = (l) => (Array.isArray(l?.images) ? l.images.filter(Boolean).length : 0);
  const created = (l) => Date.parse(l?.createdAt || l?.created_at || 0) || 0;
  const scored = new Map();
  const scoreOf = (l) => {
    if (!scored.has(l)) scored.set(l, evaluateCompleteness(listingToProfile(l)).score);
    return scored.get(l);
  };
  return [...(listings || [])].sort((a, b) => {
    const byScore = scoreOf(b) - scoreOf(a);
    if (byScore !== 0) return byScore;
    const byPhotos = photos(b) - photos(a);
    if (byPhotos !== 0) return byPhotos;
    return created(b) - created(a);
  });
}

